// Music theory data & helpers
// All intervals are semitones from the root.

export const NOTE_NAMES_SHARP = [
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
  "A",
  "A#",
  "B",
];

export const NOTE_NAMES_FLAT = [
  "C",
  "Db",
  "D",
  "Eb",
  "E",
  "F",
  "Gb",
  "G",
  "Ab",
  "A",
  "Bb",
  "B",
];

// Chromatic roots for picker — includes both sharp and flat enharmonics
export const ROOT_NOTES = [
  "C",
  "C#",
  "Db",
  "D",
  "Eb",
  "E",
  "F",
  "F#",
  "Gb",
  "G",
  "Ab",
  "A",
  "Bb",
  "B",
];

const NAME_TO_PC = {
  C: 0, "C#": 1, Db: 1,
  D: 2, "D#": 3, Eb: 3,
  E: 4, F: 5, "F#": 6, Gb: 6,
  G: 7, "G#": 8, Ab: 8,
  A: 9, "A#": 10, Bb: 10,
  B: 11,
};

// Keys that traditionally use flats
const FLAT_KEY_NAMES = new Set(["F", "Bb", "Eb", "Ab", "Db", "Gb", "Cb"]);

export function rootToPc(name) {
  return NAME_TO_PC[name] ?? 0;
}

// Choose sharp vs flat spelling for a whole key based on root name
export function isFlatKey(rootName) {
  if (!rootName) return false;
  if (rootName.includes("b")) return true;
  return FLAT_KEY_NAMES.has(rootName);
}

export function noteNamesForKey(rootName) {
  return isFlatKey(rootName) ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
}

// Scales: name -> array of semitone intervals from root
export const SCALES = {
  major: { label: "Major (Ionian)", intervals: [0, 2, 4, 5, 7, 9, 11] },
  natural_minor: {
    label: "Natural Minor (Aeolian)",
    intervals: [0, 2, 3, 5, 7, 8, 10],
  },
  harmonic_minor: {
    label: "Harmonic Minor",
    intervals: [0, 2, 3, 5, 7, 8, 11],
  },
  melodic_minor: {
    label: "Melodic Minor (asc.)",
    intervals: [0, 2, 3, 5, 7, 9, 11],
  },
  major_pentatonic: {
    label: "Major Pentatonic",
    intervals: [0, 2, 4, 7, 9],
  },
  minor_pentatonic: {
    label: "Minor Pentatonic",
    intervals: [0, 3, 5, 7, 10],
  },
  blues: { label: "Blues", intervals: [0, 3, 5, 6, 7, 10] },
  dorian: { label: "Dorian", intervals: [0, 2, 3, 5, 7, 9, 10] },
  phrygian: { label: "Phrygian", intervals: [0, 1, 3, 5, 7, 8, 10] },
  lydian: { label: "Lydian", intervals: [0, 2, 4, 6, 7, 9, 11] },
  mixolydian: { label: "Mixolydian", intervals: [0, 2, 4, 5, 7, 9, 10] },
  locrian: { label: "Locrian", intervals: [0, 1, 3, 5, 6, 8, 10] },
  whole_tone: { label: "Whole Tone", intervals: [0, 2, 4, 6, 8, 10] },
  chromatic: {
    label: "Chromatic",
    intervals: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  },
  diminished_wh: {
    label: "Diminished (W-H)",
    intervals: [0, 2, 3, 5, 6, 8, 9, 11],
  },
};

// Chords: name -> array of semitone intervals
export const CHORDS = {
  maj: { label: "Major (M)", intervals: [0, 4, 7] },
  min: { label: "Minor (m)", intervals: [0, 3, 7] },
  dim: { label: "Diminished (°)", intervals: [0, 3, 6] },
  aug: { label: "Augmented (+)", intervals: [0, 4, 8] },
  sus2: { label: "Sus2", intervals: [0, 2, 7] },
  sus4: { label: "Sus4", intervals: [0, 5, 7] },
  maj7: { label: "Major 7 (maj7)", intervals: [0, 4, 7, 11] },
  min7: { label: "Minor 7 (m7)", intervals: [0, 3, 7, 10] },
  dom7: { label: "Dominant 7 (7)", intervals: [0, 4, 7, 10] },
  m7b5: { label: "Half-diminished (m7♭5)", intervals: [0, 3, 6, 10] },
  dim7: { label: "Diminished 7 (°7)", intervals: [0, 3, 6, 9] },
  minMaj7: { label: "Minor-Major 7 (mMaj7)", intervals: [0, 3, 7, 11] },
  maj9: { label: "Major 9 (maj9)", intervals: [0, 4, 7, 11, 14] },
  min9: { label: "Minor 9 (m9)", intervals: [0, 3, 7, 10, 14] },
  dom9: { label: "Dominant 9 (9)", intervals: [0, 4, 7, 10, 14] },
  add9: { label: "Add9", intervals: [0, 4, 7, 14] },
  six: { label: "6th", intervals: [0, 4, 7, 9] },
  min6: { label: "Minor 6 (m6)", intervals: [0, 3, 7, 9] },
  maj11: { label: "Major 11 (maj11)", intervals: [0, 4, 7, 11, 14, 17] },
  min11: { label: "Minor 11 (m11)", intervals: [0, 3, 7, 10, 14, 17] },
  dom11: { label: "Dominant 11 (11)", intervals: [0, 4, 7, 10, 14, 17] },
  maj13: { label: "Major 13 (maj13)", intervals: [0, 4, 7, 11, 14, 17, 21] },
  min13: { label: "Minor 13 (m13)", intervals: [0, 3, 7, 10, 14, 17, 21] },
  dom13: { label: "Dominant 13 (13)", intervals: [0, 4, 7, 10, 14, 21] },
};

// Returns a Set of pitch classes (0-11) highlighted for a given root note + intervals
export function pitchClassesFor(rootName, intervals) {
  const rootIdx = rootToPc(rootName);
  if (rootIdx === undefined) return new Set();
  const set = new Set();
  for (const iv of intervals) {
    set.add((rootIdx + iv) % 12);
  }
  return set;
}

// White key pitch classes
export const WHITE_PCS = [0, 2, 4, 5, 7, 9, 11];
export const BLACK_PCS = [1, 3, 6, 8, 10];

export function isBlackKey(pc) {
  return BLACK_PCS.includes(pc);
}

// For black-key roots, snap to nearest white key below so the black root is
// still visible as the second key of the display.
export function nearestWhitePc(pc) {
  if (WHITE_PCS.includes(pc)) return pc;
  return (pc - 1 + 12) % 12;
}

export function noteName(pc) {
  return NOTE_NAMES_SHARP[pc];
}

// -------- Chord inversions --------
// Returns semitones FROM THE ROOT (0-based) representing the voiced chord.
// Inversion N moves the lowest N chord tones up an octave (12 semitones).
export function chordSemitones(intervals, inversion = 0) {
  let notes = [...intervals];
  notes.sort((a, b) => a - b);
  const inv = Math.max(0, Math.min(inversion, notes.length - 1));
  for (let i = 0; i < inv; i++) {
    notes[i] += 12;
  }
  return notes.sort((a, b) => a - b);
}

// Ordered note names for a chord voicing given semitones from root
export function chordNoteNames(semitonesFromRoot, rootPc, useFlats = false) {
  const names = useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  return semitonesFromRoot.map(
    (s) => names[((rootPc + s) % 12 + 12) % 12]
  );
}

// -------- Diatonic triads --------
// Given a scale root pc + scale key (7-note scales only), return the 7 triads
// built on each scale degree.
const ROMAN_UPPER = ["I", "II", "III", "IV", "V", "VI", "VII"];

function romanForDegree(degreeIdx, quality, minorContext) {
  let r = ROMAN_UPPER[degreeIdx];
  if (quality === "min" || quality === "dim") r = r.toLowerCase();
  if (quality === "dim") r += "°";
  if (quality === "aug") r += "+";
  // suppress unused param warning; kept for future context-specific styling
  void minorContext;
  return r;
}

function qualityFromIntervals(thirdInt, fifthInt) {
  if (thirdInt === 4 && fifthInt === 7) return "maj";
  if (thirdInt === 3 && fifthInt === 7) return "min";
  if (thirdInt === 3 && fifthInt === 6) return "dim";
  if (thirdInt === 4 && fifthInt === 8) return "aug";
  return "maj";
}

export function qualitySymbol(quality) {
  return quality === "maj"
    ? ""
    : quality === "min"
    ? "m"
    : quality === "dim"
    ? "°"
    : "+";
}

export function diatonicTriads(rootPc, scaleKey, useFlats = false) {
  const scale = SCALES[scaleKey];
  if (!scale || scale.intervals.length !== 7) return [];
  const names = useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  const iv = scale.intervals;
  const isMinorContext =
    scaleKey === "natural_minor" ||
    scaleKey === "harmonic_minor" ||
    scaleKey === "melodic_minor";

  const results = [];
  for (let i = 0; i < 7; i++) {
    const rootIv = iv[i];
    const thirdIv = iv[(i + 2) % 7] + (i + 2 >= 7 ? 12 : 0);
    const fifthIv = iv[(i + 4) % 7] + (i + 4 >= 7 ? 12 : 0);
    const thirdInt = (thirdIv - rootIv + 12) % 12;
    const fifthInt = (fifthIv - rootIv + 12) % 12;
    const quality = qualityFromIntervals(thirdInt, fifthInt);
    const chordRootPc = (rootPc + rootIv) % 12;
    const intervals = CHORDS[quality]
      ? CHORDS[quality].intervals
      : [0, thirdInt, fifthInt];
    results.push({
      degree: i + 1,
      roman: romanForDegree(i, quality, isMinorContext),
      rootPc: chordRootPc,
      rootName: names[chordRootPc],
      quality,
      intervals,
      label: `${names[chordRootPc]}${qualitySymbol(quality)}`,
    });
  }
  return results;
}
